import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, A11y } from 'swiper/modules';

import { useIntl } from '../../../util/reactIntl';

import css from './HistoryCarousel.module.css';
import birdImage from '../../../assets/biketribe-facebook-sharing-1200x630.jpg';
import safariImage from '../../../assets/biketribe-brandImage-1500.jpg';
import waterfallImage from '../../../assets/listingbackgroundcss.jpg';
import classNames from 'classnames';

const cards = [
  { title: 'Birdwatching', image: birdImage },
  { title: 'Jungle Safari', image: safariImage },
  { title: 'Waterfalls', image: waterfallImage },
  { title: 'Rainforest Trails', image: safariImage },
  { title: 'Hidden Valleys', image: waterfallImage },
];

import { optimizeCloudinaryUrl } from '../../../util/cloudinaryHelpers';

const getDynamicMonthYear = () => {
  const date = new Date();
  const options = { month: 'long', year: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

const HistoryCarousel = ({ images, listing }) => {
  const intl = useIntl();
  const { publicData = {} } = listing?.attributes || {};
  const screenExperience = publicData.screenExperience || 'simple_screen';
  const isGoldenScreen = screenExperience === 'golden_screen';
  const priceVariants = publicData.priceVariants || [];
  const firstVariant = priceVariants[0] || {};
  const isFlat = firstVariant && 'numberOfPeople' in firstVariant;
  const personPricing =
    (isFlat ? priceVariants : firstVariant.personPricing) ||
    (publicData.personPricing && publicData.personPricing.length > 0
      ? publicData.personPricing
      : null) ||
    [];
  const displayCards =
    images && images.length > 0
      ? images.map(img => ({
        title: img.title,
        image: optimizeCloudinaryUrl(img.url, 600),
        isTimeStampNeeded: img.isTimeStampNeeded,
        timeStampText: img.timeStampText,
      }))
      : personPricing && personPricing.length > 0
        ? personPricing.map((pricing, idx) => {
          const cloudinaryImages = publicData.cloudinaryImages || [];
          const imgObj = cloudinaryImages[idx] || cloudinaryImages[0] || {};
          const imgUrl = imgObj.url || publicData.heroImageCloudinaryUrl || waterfallImage;
          const title = imgObj.title || '';
          return {
            title,
            image: optimizeCloudinaryUrl(imgUrl, 600),
            isTimeStampNeeded: imgObj.isTimeStampNeeded,
            timeStampText: imgObj.timeStampText,
          };
        })
        : cards.map(c => ({
          ...c,
          isTimeStampNeeded: false,
          timeStampText: '',
        }));

  const [startOffset, setStartOffset] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [isMobilePagination, setIsMobilePagination] = useState(false);
  const swiperRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const readOffset = () => {
      const raw = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue('--carouselPosition');
      const parsed = parseFloat(raw);
      setStartOffset(Number.isNaN(parsed) ? 0 : parsed);
    };
    readOffset();
    window.addEventListener('resize', readOffset);
    return () => window.removeEventListener('resize', readOffset);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mobileQuery = window.matchMedia('(max-width: 640px)');
    const updatePaginationMode = event => {
      setIsMobilePagination(event.matches);
    };

    setIsMobilePagination(mobileQuery.matches);

    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', updatePaginationMode);
    } else {
      mobileQuery.addListener(updatePaginationMode);
    }

    return () => {
      if (mobileQuery.removeEventListener) {
        mobileQuery.removeEventListener('change', updatePaginationMode);
      } else {
        mobileQuery.removeListener(updatePaginationMode);
      }
    };
  }, []);

  const handleSwiper = swiper => {
    swiperRef.current = swiper;
    setActiveIndex(swiper.activeIndex || 0);
    setIsBeginning(!!swiper.isBeginning);
    setIsEnd(!!swiper.isEnd);
  };

  const handleSlideChange = swiper => {
    setActiveIndex(swiper.activeIndex || 0);
    setIsBeginning(!!swiper.isBeginning);
    setIsEnd(!!swiper.isEnd);
  };

  const handlePrev = () => {
    if (isBeginning) {
      return;
    }
    swiperRef.current?.slidePrev();
  };

  const handleNext = () => {
    if (isEnd) {
      return;
    }
    swiperRef.current?.slideNext();
  };

  const handlePaginationClick = index => {
    swiperRef.current?.slideTo(index);
  };

  const showArrows = displayCards.length > 3;
  const showPagination = displayCards.length > 1;
  const isPrevDisabled = isBeginning;
  const isNextDisabled = isEnd;

  return (
    <div className={classNames(css.root, css.historyCarouselWrapper)}>
      <Swiper
        modules={[A11y]}
        onSwiper={handleSwiper}
        onSlideChange={handleSlideChange}
        speed={800}
        loop={false}
        slidesPerView="auto"
        spaceBetween={30}
        slidesOffsetBefore={isMobilePagination ? 0 : startOffset}
        className={css.swiper}
        centeredSlides={false}
        breakpoints={{
          280: {
            spaceBetween: 16,
            slidesPerView: 1.2,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0,
          },
          640: {
            spaceBetween: 20,
            slidesPerView: 'auto',
          },
          1024: {
            spaceBetween: 30,
            slidesPerView: 'auto',
          },
        }}
        initialSlide={0}
      >
        {displayCards.map((card, idx) => (
          <SwiperSlide key={`${card.title}-${idx}`} className={css.slide}>
            <article className={css.card}>
              <img
                className={css.cardImage}
                src={optimizeCloudinaryUrl(card.image, 1200)}
                srcSet={
                  card.image &&
                    typeof card.image === 'string' &&
                    card.image.includes('res.cloudinary.com')
                    ? [600, 900, 1200, 1600]
                      .map(w => `${optimizeCloudinaryUrl(card.image, w)} ${w}w`)
                      .join(', ')
                    : undefined
                }
                sizes="400px"
                alt={card.title}
                loading="lazy"
                decoding="async"
                width="400"
                height="533"
              />

              {card.isTimeStampNeeded && card.timeStampText ? (
                <h3 className={css.cardTitle}>{card.timeStampText}</h3>
              ) : null}
            </article>
          </SwiperSlide>
        ))}
      </Swiper>

      {isMobilePagination
        ? showPagination && (
          <div className={css.pagination}>
            {displayCards.map((card, idx) => (
              <button
                key={`${card.title}-pagination-${idx}`}
                type="button"
                className={classNames(css.paginationButton, {
                  [css.paginationButtonActive]: idx === activeIndex,
                })}
                aria-label={`Go to slide ${idx + 1}`}
                aria-current={idx === activeIndex ? 'true' : undefined}
                onClick={() => handlePaginationClick(idx)}
              />
            ))}
          </div>
        )
        : showArrows && (
          <div className={css.controls} style={{ paddingLeft: 'var(--carouselPosition)' }}>
            <button
              type="button"
              className={classNames(css.arrowButton, { [css.arrowButtonDisabled]: isPrevDisabled })}
              aria-label="Previous"
              aria-disabled={isPrevDisabled}
              disabled={isPrevDisabled}
              onClick={handlePrev}
            >
              <span className={css.arrowLeft} />
            </button>
            <button
              type="button"
              className={classNames(css.arrowButton, { [css.arrowButtonDisabled]: isNextDisabled })}
              aria-label="Next"
              aria-disabled={isNextDisabled}
              disabled={isNextDisabled}
              onClick={handleNext}
            >
              <span className={css.arrowRight} />
            </button>
          </div>
        )}
    </div>
  );
};

export default HistoryCarousel;
